import { DocSignature, ParsedDocument, SignatureVerifier } from '../core/types';

/**
 * Supplies trusted public keys out-of-band (the QR doesn't carry them).
 * For the CIN this is the MJSP ES512 key; provide it as a JWK.
 */
export interface KeyProvider {
  /** Public key for verifying CIN (MJSP) JWTs, or null if unavailable. */
  cinPublicKey(): Promise<JsonWebKey | null>;
}

/** Key provider backed by a single in-memory JWK (or a lazy getter). */
export class StaticKeyProvider implements KeyProvider {
  constructor(private readonly jwk: JsonWebKey | null | (() => JsonWebKey | null)) {}
  async cinPublicKey(): Promise<JsonWebKey | null> {
    return typeof this.jwk === 'function' ? this.jwk() : this.jwk;
  }
}

function hexToBytes(hex: string): Uint8Array {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(hex.substr(i * 2, 2), 16);
  }
  return out;
}

/**
 * Verifies a CIN's ES512 JWT signature with WebCrypto (browser + Node ≥ 20).
 * The JOSE signature is raw r‖s, exactly the IEEE-P1363 form WebCrypto expects.
 */
export class CinSignatureVerifier implements SignatureVerifier {
  readonly id = 'br-cin-es512';

  constructor(private readonly keys: KeyProvider) {}

  supports(doc: ParsedDocument): boolean {
    return doc.kind === 'cin' && !!doc.signature?.signedData;
  }

  async verify(doc: ParsedDocument): Promise<DocSignature> {
    const sig = doc.signature!;
    const jwk = await this.keys.cinPublicKey();
    if (!jwk) {
      return { ...sig, status: 'unverified', detail: 'No MJSP public key provided.' };
    }
    try {
      const key = await crypto.subtle.importKey(
        'jwk',
        jwk,
        { name: 'ECDSA', namedCurve: 'P-521' },
        false,
        ['verify'],
      );
      const ok = await crypto.subtle.verify(
        { name: 'ECDSA', hash: 'SHA-512' },
        key,
        new Uint8Array(hexToBytes(sig.hex)),
        new Uint8Array(sig.signedData!),
      );
      return {
        ...sig,
        status: ok ? 'verified' : 'invalid',
        detail: ok ? 'Signed by MJSP (ES512).' : 'Signature did not verify — possible forgery.',
      };
    } catch (e) {
      return { ...sig, status: 'unverified', detail: `Verification error: ${String(e)}` };
    }
  }
}
